// Path-based routing for the parent web SPA.
//
// The web app is a single RN-Web tree mounted on `/`. A Next `fallback`
// rewrite (see next.config.js) serves `/` for any unmatched path, so real
// paths like /activity work on refresh / deep-link without a 404. Here we
// read/write the path via the History API, so navigation updates the address
// bar to a clean URL (no `#`) and browser back/forward fire `popstate`.
//
// NOTE: the exported names are historical (getHashPath/setHashPath/
// subscribeHash) — they now operate on location.pathname, not the hash. Kept
// as-is so the two callers (WebShell, WebApp) don't need to change.

/** Current pathname, normalized (no trailing slash; root is `/`). */
export function getHashPath(): string {
  if (typeof window === "undefined") return "/";
  const path = window.location.pathname || "/";
  return path.length > 1 ? path.replace(/\/+$/, "") : "/";
}

/**
 * Write the path. No-ops when it already matches (avoids duplicate history
 * entries). `replace` swaps the current entry instead of pushing a new one.
 * The query string is dropped — navigation should clear one-shot params
 * (?action=, ?subscription=), which are read once on mount.
 */
export function setHashPath(path: string, replace = false): void {
  if (typeof window === "undefined") return;
  const target = path.startsWith("/") ? path : "/" + path;
  const norm = target.length > 1 ? target.replace(/\/+$/, "") : "/";
  if (getHashPath() === norm) return;
  if (replace) {
    window.history.replaceState(null, "", norm);
  } else {
    window.history.pushState(null, "", norm);
  }
}

/** Subscribe to browser back/forward. Returns an unsubscribe fn. */
export function subscribeHash(fn: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  // pushState/replaceState don't fire popstate (so there's no self-sync
  // loop) — only browser back/forward and history.go do, which is exactly
  // when we want to re-read the path.
  window.addEventListener("popstate", fn);
  return () => window.removeEventListener("popstate", fn);
}
